/**
 * useDashboardStatistics Hook
 *
 * Calculates dashboard statistics and 7-day trend data from alerts.
 * Memoized to avoid recalculation on unrelated renders.
 */

import { useMemo } from 'react';
import type { EnhancedAlert, ChartDataPoint, DashboardStatistics } from '../../types/dashboard';
import type { TrendData } from '../../components/dashboard/TrendIndicator';

/**
 * Trend data for all stat types
 */
export interface StatTrends {
  monitored?: TrendData;
  critical?: TrendData;
  warning?: TrendData;
  healthy?: TrendData;
}

/**
 * Input parameters for useDashboardStatistics
 */
export interface UseDashboardStatisticsInput {
  /** Current alerts (including optimistic updates) */
  alerts: EnhancedAlert[];
  /** Total count of tracked products/variants */
  trackedProductsCount: number;
  /** Historical alert data for trend calculation */
  alertTrendsData: ChartDataPoint[];
}

/**
 * Return type for useDashboardStatistics hook
 */
export interface UseDashboardStatisticsReturn {
  /** Calculated dashboard statistics */
  statistics: DashboardStatistics;
  /** Week-over-week trend data (undefined if insufficient data) */
  trends: StatTrends | undefined;
}

/**
 * Hook for calculating dashboard statistics and trends
 *
 * @param input - Alerts, product count, and trend data
 * @returns Memoized statistics and trend calculations
 */
export function useDashboardStatistics({
  alerts,
  trackedProductsCount,
  alertTrendsData,
}: UseDashboardStatisticsInput): UseDashboardStatisticsReturn {
  // Calculate dashboard statistics - memoized to avoid recalculation on unrelated renders
  const statistics = useMemo<DashboardStatistics>(() => {
    const variantsWithAlerts = new Set(
      alerts
        .filter((a) => a.severity === 'critical' || a.severity === 'warning')
        .map((a) => a.variant_id)
    );

    return {
      productsMonitored: trackedProductsCount,
      criticalAlerts: alerts.filter((a) => a.severity === 'critical').length,
      warningAlerts: alerts.filter((a) => a.severity === 'warning').length,
      healthyStock: Math.max(0, trackedProductsCount - variantsWithAlerts.size),
      totalAlerts: alerts.length,
    };
  }, [alerts, trackedProductsCount]);

  // Calculate trend data by comparing current stats to 7-day prior period
  const trends = useMemo((): StatTrends | undefined => {
    // Need at least 8 days of data to calculate week-over-week trends
    if (!alertTrendsData || alertTrendsData.length < 8) {
      return undefined;
    }

    // Get today's data (most recent) and 7 days ago
    const sortedData = [...alertTrendsData].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    const today = sortedData[0];
    const weekAgo = sortedData[7] || sortedData[sortedData.length - 1];

    if (!today || !weekAgo) {
      return undefined;
    }

    // Calculate percentage changes
    const calcChange = (
      current: number,
      prior: number
    ): { value: number; direction: 'up' | 'down' | 'neutral' } => {
      if (prior === 0 && current === 0) return { value: 0, direction: 'neutral' };
      if (prior === 0) return { value: 100, direction: 'up' };

      const change = ((current - prior) / prior) * 100;
      return {
        value: Math.abs(change),
        direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
      };
    };

    const criticalChange = calcChange(today.critical, weekAgo.critical);
    const warningChange = calcChange(today.warning, weekAgo.warning);

    // For monitored products and healthy stock, use current counts comparison
    // These don't have historical data in alertTrendsData, so show neutral
    return {
      monitored: undefined, // No historical data for this metric
      critical: {
        value: criticalChange.value,
        direction: criticalChange.direction,
        period: 'vs. last week',
      },
      warning: {
        value: warningChange.value,
        direction: warningChange.direction,
        period: 'vs. last week',
      },
      healthy: undefined, // No historical data for this metric
    };
  }, [alertTrendsData]);

  return { statistics, trends };
}
